
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import type {
  WizardAnalyticsErrorCategory,
  WizardCompletionStatus,
  WizardDismissSource,
  WizardEntrypoint,
  WizardFieldChangeSource,
  WizardFieldGroup,
  WizardHelperType,
  WizardNavigationAction,
  WizardServiceMode,
  WizardServicePreset,
  WizardSummaryEditField,
  WizardStepId,
  WizardValidationReason,
} from '../../services/eventTypes';
import type { HubWeight, OperatingPeriodUtc, PassengerTypeDistribution, ServiceMode, ServicePreset, WizardState } from './types';

/** Bump when wizard step semantics or field groups change (for GA4 versioning). */
export const WIZARD_ANALYTICS_VERSION = '3';

const STEP_ORDER: readonly WizardStepId[] = [
  'setup',
  'configuration',
  'demand',
  'hub_weighting',
  'fleet',
  'service_configuration',
  'summary',
] as const;

export function getWizardStepId(step: number | WizardStepId): WizardStepId {
  if (typeof step === 'string') {
    return step;
  }
  const clamped = Math.min(Math.max(step, 1), STEP_ORDER.length);
  return STEP_ORDER[clamped - 1];
}

export function getWizardStepIndex(stepId: WizardStepId): number {
  const idx = STEP_ORDER.indexOf(stepId);
  return idx >= 0 ? idx + 1 : 1;
}

export function createWizardSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `wiz_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function mapWizardValidationReason(reason: string | undefined): WizardValidationReason {
  if (!reason) return 'unknown';
  const normalized = reason.trim().toLowerCase();
  if (normalized.includes('selectservicepreset') || normalized.includes('preset') || normalized.includes('setup')) return 'missing_preset';
  if (normalized.includes('chooseservicemode') || normalized.includes('service mode')) return 'missing_service_mode';
  if (normalized.includes('atleastonezone') || normalized.includes('polygon') || normalized.includes('service-area')) return 'missing_service_area_polygon';
  if (normalized.includes('atleastonehub') || normalized.includes('hubsplit') || normalized.includes('hub')) return 'missing_required_hubs';
  if (normalized.includes('triplimit') || normalized.includes('entertrips') || normalized.includes('generatetrips') || normalized.includes('waitfordemand') || normalized.includes('trips') || normalized.includes('help me')) return 'missing_demand_trips';
  if (normalized.includes('passengersplit') || (normalized.includes('passenger') && normalized.includes('100'))) return 'invalid_passenger_split';
  if (normalized.includes('weighting')) return 'invalid_hub_weighting';
  if (normalized.includes('vehicleseats') || normalized.includes('wheelchairrequired') || normalized.includes('vehicle') || normalized.includes('capacities')) return 'invalid_fleet_inputs';
  if (normalized.includes('maxtriptime') || normalized.includes('max trip')) return 'invalid_max_trip_time';
  if (normalized.includes('operatingperiods') || normalized.includes('operating') || normalized.includes('utc')) return 'invalid_operating_periods';
  if (normalized.includes('simulationname') || normalized.includes('simulation name')) return 'missing_simulation_name';
  if (normalized.includes('step 1')) return 'invalid_step1_combined_config';
  return 'unknown';
}

export function categorizeWizardHelperError(error: unknown): { category: WizardAnalyticsErrorCategory; code: string } {
  const message = error instanceof Error ? error.message : String(error);
  const normalized = message.toLowerCase();
  let category: WizardAnalyticsErrorCategory = 'unknown';
  if (normalized.includes('network') || normalized.includes('fetch')) {
    category = 'network';
  } else if (normalized.includes('timeout') || normalized.includes('timed out')) {
    category = 'timeout';
  } else if (normalized.includes('draw') || normalized.includes('polygon') || normalized.includes('required')) {
    category = 'validation';
  }
  return { category, code: message.slice(0, 120) };
}

/** Snapshot of wizard fields used for default-vs-manual analytics (no PII). */
export interface WizardDefaultSnapshot {
  servicePreset: ServicePreset | null;
  serviceMode: ServiceMode | null;
  polygonCount: number;
  operatingPeriodsUtc: OperatingPeriodUtc[];
  demandTrips: number | null;
  rideSource: WizardState['rideSource'];
  passengerDistribution: PassengerTypeDistribution;
  hubWeights: HubWeight[];
  vehicleCount: number;
  capacityStandard: number;
  capacityWheelchair: number;
  fleetSource: WizardState['fleetSource'];
  feelLevel: 1 | 2 | 3 | 4 | 5;
  maxTripTimeMinutes: number;
}

export function buildWizardDefaultSnapshot(state: WizardState, polygonCount: number): WizardDefaultSnapshot {
  return {
    servicePreset: state.servicePreset,
    serviceMode: state.serviceMode,
    polygonCount,
    operatingPeriodsUtc: state.operatingPeriodsUtc.map((p) => ({ ...p })),
    demandTrips: state.demandTrips,
    rideSource: state.rideSource,
    passengerDistribution: { ...state.passengerDistribution },
    hubWeights: state.hubWeights.map((h) => ({ ...h, name: '' })),
    vehicleCount: state.vehicleCount,
    capacityStandard: state.capacityStandard,
    capacityWheelchair: state.capacityWheelchair,
    fleetSource: state.fleetSource,
    feelLevel: state.feelLevel,
    maxTripTimeMinutes: state.maxTripTimeMinutes,
  };
}

function periodsEqual(a: OperatingPeriodUtc[], b: OperatingPeriodUtc[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((period, index) => {
    const other = b[index];
    return period.id === other.id && period.startUtc === other.startUtc && period.endUtc === other.endUtc;
  });
}

function hubWeightsEqual(a: HubWeight[], b: HubWeight[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((hub, index) => {
    const other = b[index];
    return hub.id === other.id && hub.percentage === other.percentage;
  });
}

export function diffWizardFieldGroups(
  baseline: WizardDefaultSnapshot,
  state: WizardState,
  polygonCount: number
): WizardFieldGroup[] {
  const changed: WizardFieldGroup[] = [];

  if (
    baseline.servicePreset !== state.servicePreset ||
    baseline.serviceMode !== state.serviceMode ||
    baseline.polygonCount !== polygonCount ||
    baseline.hubWeights.length !== state.hubWeights.length
  ) {
    changed.push('step1_combined_config');
  }

  if (baseline.demandTrips !== state.demandTrips) {
    changed.push('demand_input');
  }

  if (baseline.rideSource !== state.rideSource) {
    changed.push('ride_source_selection');
  }

  if (state.servicePreset !== 'paratransit') {
    changed.push('passenger_step_skipped');
  }

  if (
    baseline.passengerDistribution.standardPct !== state.passengerDistribution.standardPct ||
    baseline.passengerDistribution.wheelchairPct !== state.passengerDistribution.wheelchairPct
  ) {
    changed.push('passenger_distribution');
  }

  if (!hubWeightsEqual(baseline.hubWeights, state.hubWeights)) {
    changed.push('hub_weighting');
  }

  if (
    baseline.vehicleCount !== state.vehicleCount ||
    baseline.capacityStandard !== state.capacityStandard ||
    baseline.capacityWheelchair !== state.capacityWheelchair
  ) {
    changed.push('fleet_manual_inputs');
  }

  if (baseline.fleetSource !== state.fleetSource) {
    changed.push('fleet_source_selection');
  }

  if (!periodsEqual(baseline.operatingPeriodsUtc, state.operatingPeriodsUtc)) {
    changed.push('operating_hours_summary_edit');
  }

  if (baseline.feelLevel !== state.feelLevel) {
    changed.push('service_feel');
  }
  if (baseline.maxTripTimeMinutes !== state.maxTripTimeMinutes) {
    changed.push('max_trip_time');
  }

  return Array.from(new Set(changed));
}

const FIELD_GROUP_OWNER_STEP: Record<WizardFieldGroup, WizardStepId> = {
  step1_combined_config: 'setup',
  demand_input: 'demand',
  ride_source_selection: 'demand',
  passenger_step_skipped: 'setup',
  passenger_distribution: 'demand',
  hub_weighting: 'hub_weighting',
  fleet_manual_inputs: 'fleet',
  fleet_source_selection: 'fleet',
  fleet_helper_usage: 'fleet',
  operating_hours_summary_edit: 'summary',
  service_feel: 'service_configuration',
  max_trip_time: 'service_configuration',
  simulation_name_field: 'summary',
};

const ALL_TRACKED_FIELD_GROUPS: readonly WizardFieldGroup[] = [
  'step1_combined_config',
  'demand_input',
  'ride_source_selection',
  'passenger_step_skipped',
  'passenger_distribution',
  'hub_weighting',
  'fleet_manual_inputs',
  'fleet_source_selection',
  'fleet_helper_usage',
  'operating_hours_summary_edit',
  'service_feel',
  'max_trip_time',
  'simulation_name_field',
];

export function emitWizardDefaultRetained(params: {
  wizardSessionId: string;
  completionStatus: WizardCompletionStatus;
  changedGroups: ReadonlySet<WizardFieldGroup>;
}): void {
  const timestamp = new Date().toISOString();
  ALL_TRACKED_FIELD_GROUPS.forEach((fieldGroup) => {
    if (params.changedGroups.has(fieldGroup)) {
      return;
    }
    const stepId = FIELD_GROUP_OWNER_STEP[fieldGroup];
    const stepIndex = getWizardStepIndex(stepId);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_default_retained, {
      wizard_session_id: params.wizardSessionId,
      step_id: stepId,
      step_index: stepIndex,
      field_group: fieldGroup,
      completion_status: params.completionStatus,
      timestamp,
    });
  });
}

export function trackWizardOpened(params: {
  wizardSessionId: string;
  entrypoint: WizardEntrypoint;
  stepId?: WizardStepId;
  stepIndex?: number;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_opened, {
    wizard_session_id: params.wizardSessionId,
    entrypoint: params.entrypoint,
    wizard_version: WIZARD_ANALYTICS_VERSION,
    timestamp: new Date().toISOString(),
    step_id: params.stepId,
    step_index: params.stepIndex,
  });
}

export function trackWizardStepViewed(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  preset?: WizardServicePreset;
  serviceMode?: WizardServiceMode;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_step_viewed, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    timestamp: new Date().toISOString(),
    preset: params.preset ?? undefined,
    service_mode: params.serviceMode ?? undefined,
  });
}

export function trackWizardNavigationClicked(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  action: WizardNavigationAction;
  targetStepId?: WizardStepId;
  targetStepIndex?: number;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_navigation_clicked, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    action: params.action,
    timestamp: new Date().toISOString(),
    target_step_id: params.targetStepId,
    target_step_index: params.targetStepIndex,
  });
}

export function trackWizardStepCompleted(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  stepDurationMs: number;
  usedHelper?: boolean;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_step_completed, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    step_duration_ms: params.stepDurationMs,
    timestamp: new Date().toISOString(),
    used_helper: params.usedHelper,
  });
}

export function trackWizardDismissed(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  elapsedSessionMs: number;
  dismissSource?: WizardDismissSource;
  stepDurationMs?: number;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_dismissed, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    elapsed_session_ms: params.elapsedSessionMs,
    timestamp: new Date().toISOString(),
    dismiss_source: params.dismissSource,
    step_duration_ms: params.stepDurationMs,
  });
}

export function trackWizardCompleted(params: {
  wizardSessionId: string;
  elapsedSessionMs: number;
  finalStepId?: WizardStepId;
  preset?: WizardServicePreset;
  serviceMode?: WizardServiceMode;
  stepDurationMs?: number;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_completed, {
    wizard_session_id: params.wizardSessionId,
    elapsed_session_ms: params.elapsedSessionMs,
    timestamp: new Date().toISOString(),
    final_step_id: params.finalStepId,
    preset: params.preset,
    service_mode: params.serviceMode,
    step_duration_ms: params.stepDurationMs,
  });
}

export function trackWizardFieldGroupChanged(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  fieldGroup: WizardFieldGroup;
  changeSource?: WizardFieldChangeSource;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_field_group_changed, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    field_group: params.fieldGroup,
    timestamp: new Date().toISOString(),
    change_source: params.changeSource,
  });
}
export function trackWizardHelperStart(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  helperType: WizardHelperType;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_helper_start, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    helper_type: params.helperType,
    timestamp: new Date().toISOString(),
  });
}

export function trackWizardHelperComplete(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  helperType: WizardHelperType;
  becameAuthoritative?: boolean;
  authoritativeRideLayerSet?: boolean;
  authoritativeFleetSet?: boolean;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_helper_complete, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    helper_type: params.helperType,
    timestamp: new Date().toISOString(),
    became_authoritative: params.becameAuthoritative,
    authoritative_ride_layer_set: params.authoritativeRideLayerSet,
    authoritative_fleet_set: params.authoritativeFleetSet,
  });
}

export function trackWizardHelperFail(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  helperType: WizardHelperType;
  errorCategory: WizardAnalyticsErrorCategory;
  errorCode?: string;
  retryable?: boolean;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_helper_fail, {
    feature_area: 'wizard',
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    helper_type: params.helperType,
    error_category: params.errorCategory,
    timestamp: new Date().toISOString(),
    error_code: params.errorCode,
    retryable: params.retryable,
  });
}

export function trackWizardValidationBlocked(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  validationReason: WizardValidationReason;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.wizard_validation_blocked, {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    validation_reason: params.validationReason,
    timestamp: new Date().toISOString(),
  });
}

export function toServicePreset(preset: ServicePreset | null): WizardServicePreset | undefined {
  if (!preset) return undefined;
  return preset as WizardServicePreset;
}

export function toServiceMode(mode: ServiceMode | null): WizardServiceMode | undefined {
  if (!mode) return undefined;
  return mode as WizardServiceMode;
}

function trackWizardSummaryEditEvent(eventName: 'wizard_summary_edit_opened' | 'wizard_summary_edit_saved' | 'wizard_summary_edit_canceled', params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  field: WizardSummaryEditField;
}): void {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES[eventName], {
    wizard_session_id: params.wizardSessionId,
    step_id: params.stepId,
    step_index: params.stepIndex,
    field: params.field,
    timestamp: new Date().toISOString(),
  });
}

export function trackWizardSummaryEditOpened(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  field: WizardSummaryEditField;
}): void {
  trackWizardSummaryEditEvent('wizard_summary_edit_opened', params);
}

export function trackWizardSummaryEditSaved(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  field: WizardSummaryEditField;
}): void {
  trackWizardSummaryEditEvent('wizard_summary_edit_saved', params);
}

export function trackWizardSummaryEditCanceled(params: {
  wizardSessionId: string;
  stepIndex: number;
  stepId: WizardStepId;
  field: WizardSummaryEditField;
}): void {
  trackWizardSummaryEditEvent('wizard_summary_edit_canceled', params);
}
